import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Wallet } from "lucide-react";

export function PayoutCard() {
  const { user, refreshUser } = useAuth();
  const { t } = useT();
  const [saving, setSaving] = useState(false);
  const [pm, setPm] = useState({
    orange_money: { enabled: false, number: "" },
    wave_money: { enabled: false, number: "" },
    moov_money: { enabled: false, number: "" },
    bank: { enabled: false, rib: "" },
  });
  useEffect(() => {
    const p = user?.payout_methods || {};
    setPm({
      orange_money: { enabled: !!p.orange_money?.enabled, number: p.orange_money?.number || "" },
      wave_money: { enabled: !!p.wave_money?.enabled, number: p.wave_money?.number || "" },
      moov_money: { enabled: !!p.moov_money?.enabled, number: p.moov_money?.number || "" },
      bank: { enabled: !!p.bank?.enabled, rib: p.bank?.rib || "" },
    });
  }, [user?.payout_methods]);

  const setEnabled = (key, v) => setPm((s) => ({ ...s, [key]: { ...s[key], enabled: v } }));
  const setVal = (key, field, v) => setPm((s) => ({ ...s, [key]: { ...s[key], [field]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/me/profile", { payout_methods: pm });
      await refreshUser();
      toast.success(t("dashboard.payoutSaved"));
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const rows = [
    { key: "orange_money", label: t("dashboard.payoutOrange"), field: "number", ph: t("dashboard.payoutPhone") },
    { key: "wave_money", label: t("dashboard.payoutWave"), field: "number", ph: t("dashboard.payoutPhone") },
    { key: "moov_money", label: t("dashboard.payoutMoov"), field: "number", ph: t("dashboard.payoutPhone") },
    { key: "bank", label: t("dashboard.payoutBank"), field: "rib", ph: t("dashboard.payoutRib") },
  ];

  return (
    <div className="rounded-2xl bg-card border border-border p-5" data-testid="payout-card">
      <div className="flex items-center gap-2">
        <Wallet className="h-5 w-5 text-primary" />
        <h2 className="font-heading font-semibold text-lg text-secondary">{t("dashboard.payoutTitle")}</h2>
      </div>
      <p className="text-sm text-muted-foreground mt-1">{t("dashboard.payoutHint")}</p>
      <div className="mt-4 space-y-3">
        {rows.map((r) => (
          <div key={r.key} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <label className="flex items-center gap-2.5 cursor-pointer sm:w-56 shrink-0" data-testid={`payout-check-${r.key}`}>
              <Checkbox checked={pm[r.key].enabled} onCheckedChange={(v) => setEnabled(r.key, !!v)} />
              <span className="text-sm font-medium text-foreground">{r.label}</span>
            </label>
            {pm[r.key].enabled && (
              <Input value={pm[r.key][r.field]} onChange={(e) => setVal(r.key, r.field, e.target.value)}
                placeholder={r.ph} className="flex-1" data-testid={`payout-input-${r.key}`} />
            )}
          </div>
        ))}
      </div>
      <Button onClick={save} disabled={saving} className="mt-4 rounded-full bg-secondary hover:bg-secondary/90" data-testid="payout-save-btn">
        {t("dashboard.payoutSave")}
      </Button>
    </div>
  );
}
